import { useState, type SyntheticEvent } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { docUrl } from "../../lib/docLinks";
import {
  Button,
  HelpTip,
  Inline,
  InlineError,
  Select,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../../ui";

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertChannelCreate = components["schemas"]["AlertChannelCreate"];
type AlertChannelPatch = components["schemas"]["AlertChannelPatch"];
type AlertChannelType = components["schemas"]["AlertChannelType"];
type SigningSecretAction = "keep" | "replace" | "remove";
type IncidentIoAuthMode =
  components["schemas"]["IncidentIoAlertChannelConfig"]["auth_mode"];

const CHANNEL_LABELS: Record<AlertChannelType, string> = {
  email: "Email",
  incident_io: "incident.io",
  pagerduty: "PagerDuty",
  slack: "Slack",
  webhook: "Webhook",
};

interface SharedAlertChannelFormProps {
  availableChannelTypes: AlertChannelType[];
  errorMessage?: string;
  isPending: boolean;
  onCancel: () => void;
  submitLabel: string;
}

type AlertChannelFormProps =
  | (SharedAlertChannelFormProps & {
      channel?: never;
      onSubmit: (body: AlertChannelCreate) => void;
    })
  | (SharedAlertChannelFormProps & {
      channel: AlertChannel;
      onSubmit: (body: AlertChannelPatch) => void;
    });

function emailDefaults(channel?: AlertChannel): {
  fromIdentity: string;
  toAddresses: string;
} {
  const config = channel?.config;
  if (config != null && "to_addresses" in config) {
    return {
      fromIdentity: config.from_identity,
      toAddresses: config.to_addresses.join(", "),
    };
  }
  return { fromIdentity: "", toAddresses: "" };
}

function webhookHeaders(channel?: AlertChannel): Record<string, string> | null {
  const config = channel?.config;
  return config != null && "url_hint" in config ? config.headers : null;
}

function incidentIoDefaults(channel?: AlertChannel): {
  alertSourceConfigId: string;
  authMode: IncidentIoAuthMode;
  tokenSecretRefHint: string | null;
} {
  const config = channel?.config;
  if (config != null && "alert_source_config_id" in config) {
    return {
      alertSourceConfigId: config.alert_source_config_id,
      authMode: config.auth_mode,
      tokenSecretRefHint: config.token_secret_ref_hint,
    };
  }
  return {
    alertSourceConfigId: "",
    authMode: "bearer",
    tokenSecretRefHint: null,
  };
}

function hint(
  channel: AlertChannel | undefined,
  name: "routing_key_hint" | "signing_secret_hint" | "url_hint",
): string | null {
  const config = channel?.config;
  if (config == null) {
    return null;
  }
  if (name === "routing_key_hint") {
    return "routing_key_hint" in config ? config.routing_key_hint : null;
  }
  if (!("url_hint" in config)) {
    return null;
  }
  return name === "url_hint" ? config.url_hint : config.signing_secret_hint;
}

function secretDescription(currentHint: string | null): string {
  return currentHint == null
    ? "Stored encrypted. Never shown again."
    : `Current: ${currentHint}. Blank keeps it.`;
}

function signingSecretDescription(currentHint: string | null): string {
  return currentHint == null
    ? "No signing secret is set."
    : `Current: ${currentHint}.`;
}

function addresses(value: string): string[] {
  return value
    .split(/[\s,]+/u)
    .map((address) => address.trim())
    .filter((address) => address.length > 0);
}

export function AlertChannelForm(props: AlertChannelFormProps) {
  const {
    availableChannelTypes,
    channel,
    errorMessage,
    isPending,
    onCancel,
    submitLabel,
  } = props;
  const queryClient = useQueryClient();
  const toast = useToast();
  const email = emailDefaults(channel);
  const incidentIo = incidentIoDefaults(channel);
  const existingSlackConfig =
    channel?.config != null && "channel_id" in channel.config
      ? channel.config
      : null;
  const [channelName, setChannelName] = useState(channel?.display_name ?? "");
  const [channelType, setChannelType] = useState<AlertChannelType>(
    channel?.channel_type ?? availableChannelTypes[0] ?? "email",
  );
  const [slackQuery, setSlackQuery] = useState("");
  const [slackChannelId, setSlackChannelId] = useState(
    existingSlackConfig?.channel_id ?? "",
  );
  const [slackChannelName, setSlackChannelName] = useState(
    existingSlackConfig?.channel_name ?? "",
  );
  const [pagerDutyRoutingKey, setPagerDutyRoutingKey] = useState("");
  const [fromIdentity, setFromIdentity] = useState(email.fromIdentity);
  const [toAddresses, setToAddresses] = useState(email.toAddresses);
  const [alertSourceConfigId, setAlertSourceConfigId] = useState(
    incidentIo.alertSourceConfigId,
  );
  const [incidentIoAuthMode, setIncidentIoAuthMode] =
    useState<IncidentIoAuthMode>(incidentIo.authMode);
  const [incidentIoTokenSecretRef, setIncidentIoTokenSecretRef] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [signingSecretAction, setSigningSecretAction] =
    useState<SigningSecretAction>("keep");
  const currentSigningSecretHint = hint(channel, "signing_secret_hint");
  const isChangingType =
    channel != null && channelType !== channel.channel_type;
  const isEditingWebhook =
    channel != null && !isChangingType && channel.channel_type === "webhook";
  const isEditingIncidentIo =
    channel != null &&
    !isChangingType &&
    channel.channel_type === "incident_io";
  const incidentIoPublicConfigChanged =
    alertSourceConfigId.trim() !== incidentIo.alertSourceConfigId ||
    incidentIoAuthMode !== incidentIo.authMode;
  const requiresPagerDutyCredential =
    channel == null || isChangingType || pagerDutyRoutingKey.trim().length > 0;
  const requiresIncidentIoCredential =
    !isEditingIncidentIo ||
    incidentIoPublicConfigChanged ||
    incidentIoTokenSecretRef.trim().length > 0;
  const requiresWebhookUrl =
    !isEditingWebhook || signingSecretAction !== "keep";
  const slackInstallation = useQuery({
    enabled: channelType === "slack",
    queryKey: queryKeys.slack.installation,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/slack/installation");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data.installation;
    },
  });
  const slackChannels = useQuery({
    enabled: channelType === "slack" && slackInstallation.data != null,
    queryKey: queryKeys.slack.channels(slackQuery.trim()),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/slack/channels", {
        params: {
          query: {
            limit: 50,
            query: slackQuery.trim() || undefined,
          },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data.items;
    },
  });
  const connectSlack = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST("/v1/slack/authorizations");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data.authorization_url;
    },
    onSuccess: (authorizationUrl) => {
      window.open(
        authorizationUrl,
        "marker-slack-oauth",
        "popup,width=720,height=760",
      );
    },
    onError: (error) => {
      toast.error("Slack connection failed", {
        description: requestErrorDescription(
          error,
          "Could not start Slack OAuth.",
        ),
      });
    },
  });
  const refreshSlack = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.POST("/v1/slack/channel-refreshes");
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Slack channel refresh queued");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.slack.all,
      });
    },
    onError: (error) => {
      toast.error("Slack channel refresh failed", {
        description: requestErrorDescription(
          error,
          "Could not refresh Slack channels.",
        ),
      });
    },
  });

  function authoredConfig():
    | AlertChannelCreate["config"]
    | AlertChannelPatch["config"] {
    if (channelType === "slack") {
      const installationId =
        slackInstallation.data?.id ?? existingSlackConfig?.installation_id;
      return installationId == null ||
        slackChannelId.length === 0 ||
        slackChannelName.length === 0
        ? null
        : {
            installation_id: installationId,
            channel_id: slackChannelId,
            channel_name: slackChannelName,
          };
    }
    if (channelType === "email") {
      return {
        from_identity: fromIdentity.trim(),
        to_addresses: addresses(toAddresses),
      };
    }
    if (channelType === "pagerduty") {
      return pagerDutyRoutingKey.trim().length === 0
        ? null
        : { routing_key: pagerDutyRoutingKey.trim() };
    }
    if (channelType === "incident_io") {
      if (incidentIoTokenSecretRef.trim().length === 0) {
        return null;
      }
      return {
        alert_source_config_id: alertSourceConfigId.trim(),
        auth_mode: incidentIoAuthMode,
        token_secret_ref: incidentIoTokenSecretRef.trim(),
      };
    }
    if (isEditingWebhook && signingSecretAction === "keep") {
      return null;
    }
    if (webhookUrl.trim().length === 0) {
      return null;
    }
    return {
      // Header authoring stays behind a neutral seam until its product
      // representation and duplicate-name behavior are founder-ratified.
      // Existing opaque headers survive credential replacement unchanged.
      headers: webhookHeaders(channel),
      signing_secret:
        isEditingWebhook && signingSecretAction === "remove"
          ? null
          : signingSecret.trim() || null,
      url: webhookUrl.trim(),
    };
  }

  function submitChannel(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const config = authoredConfig();
    if (channel == null) {
      if (config == null) {
        return;
      }
      props.onSubmit({
        channel_type: channelType,
        config,
        display_name: channelName.trim(),
      });
      return;
    }
    props.onSubmit({
      channel_type: isChangingType ? channelType : null,
      config,
      display_name: channelName.trim(),
    });
  }

  return (
    <form onSubmit={submitChannel}>
      <Stack gap={4}>
        {errorMessage != null ? (
          <InlineError>{errorMessage}</InlineError>
        ) : null}
        <TextInput
          label="Name"
          required
          value={channelName}
          onChange={(event) => {
            setChannelName(event.currentTarget.value);
          }}
        />
        <Select
          label="Type"
          value={channelType}
          onChange={(event) => {
            setChannelType(event.currentTarget.value as AlertChannelType);
          }}
        >
          {availableChannelTypes.map((type) => (
            <option key={type} value={type}>
              {CHANNEL_LABELS[type]}
            </option>
          ))}
        </Select>
        {channelType === "slack" ? (
          <Stack gap={3}>
            {slackInstallation.data == null ? (
              <>
                <Inline gap={2}>
                  <Text>Connect Slack to choose a workspace channel.</Text>
                  <HelpTip
                    docHref={docUrl("guides/alert-slack")}
                    docLabel="Send alerts to Slack"
                    label="About connecting Slack"
                  >
                    {
                      "If the Slack app was removed from the workspace, reconnecting restores delivery to the rules that already use this channel."
                    }
                  </HelpTip>
                </Inline>
                <Inline>
                  <Button
                    disabled={connectSlack.isPending}
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      connectSlack.mutate();
                    }}
                  >
                    {existingSlackConfig == null
                      ? "Connect Slack"
                      : "Reconnect Slack"}
                  </Button>
                  <Button
                    disabled={slackInstallation.isFetching}
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      void slackInstallation.refetch();
                    }}
                  >
                    Check connection
                  </Button>
                </Inline>
              </>
            ) : (
              <>
                <Text>Connected to {slackInstallation.data.team_name}</Text>
                <TextInput
                  label="Find a channel"
                  value={slackQuery}
                  onChange={(event) => {
                    setSlackQuery(event.currentTarget.value);
                  }}
                />
                <Select
                  label="Slack channel"
                  required
                  value={slackChannelId}
                  onChange={(event) => {
                    const id = event.currentTarget.value;
                    const selected = slackChannels.data?.find(
                      (candidate) => candidate.channel_id === id,
                    );
                    setSlackChannelId(id);
                    setSlackChannelName(selected?.display_name ?? "");
                  }}
                >
                  <option value="">Choose a channel</option>
                  {existingSlackConfig != null &&
                  !slackChannels.data?.some(
                    (candidate) =>
                      candidate.channel_id === existingSlackConfig.channel_id,
                  ) ? (
                    <option value={existingSlackConfig.channel_id}>
                      #{existingSlackConfig.channel_name}
                    </option>
                  ) : null}
                  {slackChannels.data?.map((candidate) => (
                    <option
                      key={candidate.channel_id}
                      value={candidate.channel_id}
                    >
                      #{candidate.display_name}
                      {candidate.is_private ? " (private)" : ""}
                    </option>
                  ))}
                </Select>
                <Text>
                  Private channel missing? Invite the Marker app, then refresh.
                </Text>
                <Inline>
                  <Button
                    disabled={refreshSlack.isPending}
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      refreshSlack.mutate();
                    }}
                  >
                    Refresh channels
                  </Button>
                  <Button
                    disabled={slackChannels.isFetching}
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      void slackChannels.refetch();
                    }}
                  >
                    Check refresh
                  </Button>
                </Inline>
              </>
            )}
          </Stack>
        ) : null}
        {channelType === "email" ? (
          <>
            <TextInput
              label="From identity"
              required
              value={fromIdentity}
              onChange={(event) => {
                setFromIdentity(event.currentTarget.value);
              }}
            />
            <TextInput
              label="To addresses"
              required
              value={toAddresses}
              onChange={(event) => {
                setToAddresses(event.currentTarget.value);
              }}
            />
          </>
        ) : null}
        {channelType === "pagerduty" ? (
          <TextInput
            autoComplete="new-password"
            description={secretDescription(hint(channel, "routing_key_hint"))}
            label="Routing key"
            required={requiresPagerDutyCredential}
            type="password"
            value={pagerDutyRoutingKey}
            onChange={(event) => {
              setPagerDutyRoutingKey(event.currentTarget.value);
            }}
          />
        ) : null}
        {channelType === "incident_io" ? (
          <>
            <TextInput
              label="Alert source config ID"
              required
              value={alertSourceConfigId}
              onChange={(event) => {
                setAlertSourceConfigId(event.currentTarget.value);
              }}
            />
            <Select
              label="Authentication"
              value={incidentIoAuthMode}
              onChange={(event) => {
                setIncidentIoAuthMode(
                  event.currentTarget.value as IncidentIoAuthMode,
                );
              }}
            >
              <option value="basic">Basic</option>
              <option value="bearer">Bearer</option>
            </Select>
            <TextInput
              autoComplete="new-password"
              description={secretDescription(incidentIo.tokenSecretRefHint)}
              label="Token"
              required={requiresIncidentIoCredential}
              type="password"
              value={incidentIoTokenSecretRef}
              onChange={(event) => {
                setIncidentIoTokenSecretRef(event.currentTarget.value);
              }}
            />
          </>
        ) : null}
        {channelType === "webhook" ? (
          <>
            <TextInput
              autoComplete="new-password"
              description={secretDescription(hint(channel, "url_hint"))}
              disabled={isEditingWebhook && signingSecretAction === "keep"}
              label="Webhook URL"
              required={requiresWebhookUrl}
              type="password"
              value={webhookUrl}
              onChange={(event) => {
                setWebhookUrl(event.currentTarget.value);
              }}
            />
            {isEditingWebhook ? (
              <Select
                description={signingSecretDescription(currentSigningSecretHint)}
                help={
                  <HelpTip
                    docHref={docUrl("guides/alert-webhook")}
                    docLabel="Send alerts to a webhook"
                    label="About the signing secret"
                  >
                    {
                      "Keep preserves the stored secret. Replace stores a new one. Remove clears it, and Marker stops signing deliveries to this channel."
                    }
                  </HelpTip>
                }
                label="Signing secret"
                value={signingSecretAction}
                onChange={(event) => {
                  setSigningSecretAction(
                    event.currentTarget.value as SigningSecretAction,
                  );
                }}
              >
                <option value="keep">Keep current</option>
                <option value="replace">Replace</option>
                <option value="remove">Remove</option>
              </Select>
            ) : null}
            {!isEditingWebhook || signingSecretAction === "replace" ? (
              <TextInput
                autoComplete="new-password"
                description={
                  isEditingWebhook
                    ? "Stored encrypted. Never shown again."
                    : secretDescription(currentSigningSecretHint)
                }
                label={
                  isEditingWebhook ? "New signing secret" : "Signing secret"
                }
                required={isEditingWebhook}
                type="password"
                value={signingSecret}
                onChange={(event) => {
                  setSigningSecret(event.currentTarget.value);
                }}
              />
            ) : null}
          </>
        ) : null}
        <Inline>
          <Button disabled={isPending} type="submit">
            {isPending ? "Saving..." : submitLabel}
          </Button>
          <Button
            disabled={isPending}
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
